/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PlayerTable from "@/pages/players/PlayerTable";
import PlayerForm from "@/pages/players/PlayerForm";
import PlayerView from "@/pages/players/PlayerView";
import {
  usePlayersAdmin,
  useCreatePlayer,
  useUpdatePlayer,
  useDeletePlayer,
  useTogglePlayerActive,
} from "@/hooks/api/usePlayers";
import type {
  Player,
  CreatePlayerRequest,
  UpdatePlayerRequest,
  PlayerFilters,
} from "@/types/api.types";

export default function PlayerManagement() {
  // Default to Active so the page opens with the relevant working set;
  // admins can switch to "All Status" to see active + inactive together.
  const [filters, setFilters] = useState<PlayerFilters>({ page: 1, limit: 15, isActive: true });
  const [isOpen, setIsOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState<Partial<Player> | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState(false);

  const { data, isLoading, error } = usePlayersAdmin(filters);
  const { mutate: createPlayer, isPending: isCreating } = useCreatePlayer();
  const { mutate: updatePlayer, isPending: isUpdating } = useUpdatePlayer();
  const { mutate: deletePlayer, isPending: isDeleting } = useDeletePlayer();
  const { mutate: toggleActive } = useTogglePlayerActive();

  const handleAddPlayer = () => {
    setIsEditing(false);
    setCurrentPlayer({
      name: "",
      position: undefined,
      nationId: undefined,
      clubId: null,
      club: null,
      age: null,
      jerseyNumber: null,
      isActive: true,
    });
    setIsOpen(true);
  };

  const handleEditPlayer = (player: Player) => {
    setIsEditing(true);
    setCurrentPlayer(player);
    setIsOpen(true);
  };

  const handleViewPlayer = (player: Player) => {
    setCurrentPlayer(player);
    setViewOpen(true);
  };

  const handleDeletePlayer = (id: number) => {
    if (!confirm("Are you sure you want to delete this player?")) return;

    deletePlayer(id, {
      onSuccess: () => toast.success("Player deleted successfully"),
      onError: (error: any) =>
        toast.error(error.response?.data?.message || "Failed to delete player"),
    });
  };

  const handleToggleActive = (player: Player) => {
    toggleActive(
      { id: player.id, isActive: !player.isActive },
      {
        onSuccess: () =>
          toast.success(
            `Player ${player.isActive ? "deactivated" : "activated"} successfully`,
          ),
        onError: (error: any) =>
          toast.error(
            error.response?.data?.message || "Failed to update player status",
          ),
      },
    );
  };

  const handleSavePlayer = (
    formData: CreatePlayerRequest | UpdatePlayerRequest,
  ) => {
    if (isEditing && currentPlayer?.id) {
      updatePlayer(
        { id: currentPlayer.id, data: formData as UpdatePlayerRequest },
        {
          onSuccess: () => {
            toast.success("Player updated successfully");
            setIsOpen(false);
            setCurrentPlayer(null);
          },
          onError: (error: any) =>
            toast.error(
              error.response?.data?.message || "Failed to update player",
            ),
        },
      );
    } else {
      createPlayer(formData as CreatePlayerRequest, {
        onSuccess: () => {
          toast.success("Player created successfully");
          setIsOpen(false);
          setCurrentPlayer(null);
        },
        onError: (error: any) =>
          toast.error(
            error.response?.data?.message || "Failed to create player",
          ),
      });
    }
  };

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-red-600 mb-4">Failed to load players</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Player Management
          </h1>
          <p className="text-gray-500 mt-1">Manage World Cup players</p>
        </div>
        <Button
          onClick={handleAddPlayer}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          disabled={isCreating}
        >
          <Plus className="mr-2 h-4 w-4" /> Add Player
        </Button>
      </div>

      <PlayerTable
        players={data?.players || []}
        isLoading={isLoading}
        filters={filters}
        onFiltersChange={(f) => setFilters({ ...f, page: 1 })}
        onClearFilters={() => setFilters({ page: 1, limit: 15 })}
        onEdit={handleEditPlayer}
        onView={handleViewPlayer}
        onDelete={handleDeletePlayer}
        onToggleActive={handleToggleActive}
        isDeleting={isDeleting}
        pagination={data?.pagination}
        onPageChange={(page: number) => setFilters((prev) => ({ ...prev, page }))}
        onPageSizeChange={(limit: number) => setFilters((prev) => ({ ...prev, limit, page: 1 }))}
      />

      <PlayerForm
        open={isOpen}
        onOpenChange={setIsOpen}
        player={currentPlayer}
        isEditing={isEditing}
        onSave={handleSavePlayer}
        isSaving={isCreating || isUpdating}
      />

      <PlayerView
        open={viewOpen}
        onOpenChange={setViewOpen}
        player={currentPlayer as Player}
      />
    </Layout>
  );
}
